// src/pages/user/UserList.tsx
import React, { useEffect, useState } from "react";

import { User } from "../../../types/user";
import { useUserStore } from "../../../store/userStore";
import { CheckCheckIcon, Edit, Plus, Trash2 } from "lucide-react";
import { IconButton, IconTextButton, TextButton } from "../../../components/ButtonComponents";
import { FirmUserDialog } from "./FirmUserDialog";
import { toast } from "react-toastify";

import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";

import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { getTurkishRoleName } from "../../../types/role";
import { EDataTable } from "../../../components/EDataTable";
import { DataTableColumn } from "../../../types";

export const FirmUserList = () => {
  const { deleteItem, createItem, updateItem, fetchDataDynamic: fetchUsers } = useUserStore();
  const { handleApiError } = useApiErrorHandler();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [pendingOnly, setPendingOnly] = useState(false);

  const handleEdit = (user: User) => {
    setUserToEdit(user);
    setIsModalOpen(true);
  };

  const { confirm, modal: confirmModal } = useConfirmModal();
  const handleDeleteClick = async (user: User) => {
    const ok = await confirm(`${user.name} adlı kullanıcıyı silmek istiyor musun?`);

    if (ok) {
      try {
        await deleteItem(user.id);
        toast.success("Kullanıcı silindi");
      } catch (error) {
        handleApiError(error);
      }
    }
  };




  const handleNew = () => {
    setUserToEdit(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setUserToEdit(null);
  };

  const handleSaveUser = async (userData: Omit<User, "id">) => {
    // UserDialog'dan gelen veri (formData) zaten Omit<User, "id"> formatındadır.

    // NOT: Eğer DTO'da fazladan objeler (role, firm, account) varsa temizlik gerekebilir.
    const { role, firm, account, ...cleanedData } = userData as any;

    // DTO'nuz sadece ID'leri beklediği için roleId'yi userData'dan alıyoruz.
    const payload = {
      ...cleanedData,
      roleId: userData.roleId,
      firmId: userData.firmId,
      accountId: userData.accountId ?? null,
      // name, email, password, vb. restBody içinde kalır.
    };
    console.log("Payload to be sent:", payload);
    try {
      if (userToEdit) {
        // Güncelleme işlemi
        await updateItem(userToEdit.id, payload);
        toast.success(`Kullanıcı Güncellendi: ${payload.name}`);
      } else {
        // Oluşturma işlemi
        await createItem(payload);
        toast.success(`Yeni Kullanıcı Eklendi: ${payload.name}`);
      }

      // Listeyi yenile ve dialog'u kapat
      fetchUsers({}); // Listeyi güncelleyelim
      handleCloseModal();

    } catch (error: any) {
      // Hata yakalama
      // handleApiError(error); // Eğer BaseController'da hata yönetimi yoksa bunu kullanabilirsiniz.
      const errorMessage = error.message || "Kullanıcı kaydetme hatası oluştu.";
      toast.error(errorMessage);
    }
  };

  const actionBodyTemplate = (row: User) => (
    <div className="flex gap-2">
      <IconButton
        onClick={() => handleEdit(row)}
        icon={Edit}
        className="btn-sm text-blue-600 hover:text-blue-800"
      />
      <IconButton
        onClick={() => handleDeleteClick(row)}
        icon={Trash2}
        className="btn-sm text-red-500 hover:text-red-700"
      />
    </div>
  );

  const columns: DataTableColumn<User>[] = [
    { field: "pinCode", header: "PİN", width: "80px", sortable: true, align: "center" },
    { field: "name", header: "Ad Soyad" },
    { field: "email", header: "E-posta" },
    {
      field: "isActive",
      header: "Onay",
      body: (rowData: any) => {
        const isActive = rowData.isActive;
        return (
          <Tag
            severity={isActive ? "success" : "warning"}
            value={isActive ? "Onaylı" : "Bekliyor"}
          />
        );
      }
    },
    {
      field: "firm",
      header: "Firma",
      width: "180px",
      body: (rowData: any) => {
        const text = rowData.accountId
          ? rowData.account?.name
          : rowData.firm?.name;
        return (
          <div className="truncate" title={text}>
            {rowData.accountId ? (
              text
            ) : (
              <span className="flex items-center gap-1">
                <CheckCheckIcon size={16} color="green" />
                {text}
              </span>
            )}
          </div>
        );
      }
    },
    {
      field: "phone",
      header: "Telefon"
    },
    {
      field: "role.name",
      header: "Rol",
      body: (rowData: any) => getTurkishRoleName(rowData.role.name)
    },
    {
      field: "actions",
      header: "İşlemler",
      width: "120px",
      body: (rowData: any) => actionBodyTemplate(rowData)
    }
  ];

  return (
    <div className="w-full">
      <EDataTable
        useStore={useUserStore}
        title="Kullanıcılar"
        tableKey="firmUserTable"
        dataKey="id"
        placeholder="Ad, E-posta, Firma Ara..."
        searchFields={[
          { field: "name", operator: "contains" },
          { field: "email", operator: "contains" },
          { field: "firm.name", operator: "contains" },
          { field: "role.name", operator: "contains" },
          { field: "phone", operator: "contains" },
        ]}
        filters={pendingOnly ? [{ field: "isActive", operator: "eq", value: false }] : undefined}
        actions={
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
              <label className="text-sm font-semibold text-orange-700 cursor-pointer" onClick={() => setPendingOnly(!pendingOnly)}>
                Sadece Onay Bekleyenler
              </label>
              <InputSwitch checked={pendingOnly} onChange={(e) => setPendingOnly(e.value)} />
            </div>
            <TextButton onClick={() => handleNew()} className="btn-new">
              Yeni Kullanıcı
            </TextButton>
          </div>
        }
        columns={columns}
      />
      {confirmModal}


      <FirmUserDialog
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        user={userToEdit}
        onSave={handleSaveUser} currentFirmId={0} currentFirmName={""} />
    </div>
  );
};
